import React, { RefObject } from 'react';
import {
  GestureResponderEvent,
  StyleSheet,
  Platform,
  ScrollView,
  View,
  useWindowDimensions,
  Modal,
  TouchableWithoutFeedback,
} from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { hapticSelection } from 'lib/withHapticFeedback';
import { theme } from 'lib/theme';
import { useEsc } from 'hooks/useEsc';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import reactStringReplace from 'react-string-replace';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { useDispatch } from 'react-redux';
import { createAction } from 'typesafe-actions';
import { Button, ButtonProps } from './Button';
import { Icon, ICONS } from './Icon';
import { IconButton, IconButtonProps } from './IconButton';
import { SeparatorHorizontal } from './SeparatorHorizontal';
import { ViewWithSeparators } from './ViewWithSeparators';
import { Txt, TxtProps } from './Txt';
import { Pressable } from './Pressable';

type MenuRef = {
  close: () => void;
};

type DefinedMenuOption = {
  key?: string;
  text: string;
  subText?: string;
  horizontalText?: boolean;
  onPress?: () => void;
  icon?: ICONS;
  iconColor?: string;
  leftIcon?: ICONS;
  leftIconColor?: string;
  textColor?: string;
  destructive?: boolean;
  textNumberOfLines?: number;
  textSize?: TxtProps['size'];
};

export type MenuOption = DefinedMenuOption | '-' | string | React.ReactElement;

type Props = {
  visible?: boolean;
  options: MenuOption[];
  onDismiss?: () => void;
  width?: number;
  height?: number;
  disabled?: boolean;
  ref?: RefObject<MenuRef>;
};

export type MenuParentBounds = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

export type MenuAnchorPosition =
  | { type: 'point'; x: number; y: number }
  | ({ type: 'parent' } & MenuParentBounds);

type PropsWithPosition =
  | { x: number; y: number; parentBounds?: never }
  | { x?: never; y?: never; parentBounds: MenuParentBounds }
  | { x?: never; y?: never; parentBounds?: never };

type PropsWithAnchor = {
  anchor?: React.ReactElement;
  buttonConfig?: never;
  iconButtonConfig?: never;
};

type PropsWithIconButton = {
  anchor?: never;
  buttonConfig?: never;
  iconButtonConfig?: Omit<IconButtonProps, 'onPress' | 'onPressIn'>;
};

type PropsWithButton = {
  anchor?: never;
  buttonConfig?: Omit<ButtonProps, 'onPress' | 'onPressIn'>;
  iconButtonConfig?: never;
};

const isDefinedMenuOption = (option: MenuOption): option is DefinedMenuOption =>
  typeof option === 'object' && option !== null && 'text' in option;

export type MenuProps = Props &
  PropsWithPosition &
  (PropsWithIconButton | PropsWithButton | PropsWithAnchor);

const eventCloseMenus = createAction('doCloseMenus')();
const MENU_ANIMATION_DURATION = 190;
const MENU_ANCHOR_OFFSET_X = 24;
const MENU_ANCHOR_OFFSET_Y = 12;

type TouchEventWithScreenPosition = GestureResponderEvent['nativeEvent'] & {
  detail?: number;
  key?: string;
  screenX?: number;
  screenY?: number;
};

type AnchorBounds = MenuParentBounds & {
  type: 'point' | 'parent';
};

const MenuEscapeHandler = ({ onDismiss }: { onDismiss: () => void }) => {
  useEsc(onDismiss);
  return null;
};

// iOS page coordinates are modal-relative; screen coordinates stay window-relative.
export const getMenuAnchorPosition = (
  event?: GestureResponderEvent,
): MenuAnchorPosition | undefined => {
  const nativeEvent = event?.nativeEvent as
    | TouchEventWithScreenPosition
    | undefined;
  const isWebKeyboardActivation =
    Platform.OS === 'web' &&
    (nativeEvent?.key === 'Enter' ||
      nativeEvent?.key === ' ' ||
      nativeEvent?.detail === 0);
  if (
    !isWebKeyboardActivation &&
    nativeEvent?.pageX !== undefined &&
    nativeEvent.pageY !== undefined
  ) {
    return {
      type: 'point',
      x:
        Platform.OS === 'ios'
          ? (nativeEvent.screenX ?? nativeEvent.pageX)
          : nativeEvent.pageX,
      y:
        Platform.OS === 'ios'
          ? (nativeEvent.screenY ?? nativeEvent.pageY)
          : nativeEvent.pageY,
    };
  }

  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    const currentTarget = event?.currentTarget as unknown as
      | HTMLElement
      | undefined;
    const anchorElement = currentTarget?.getBoundingClientRect
      ? currentTarget
      : document.activeElement instanceof HTMLElement
        ? document.activeElement
        : undefined;
    const bounds = anchorElement?.getBoundingClientRect();
    if (bounds) {
      return {
        type: 'parent',
        left: bounds.left,
        right: bounds.right,
        top: bounds.top,
        bottom: bounds.bottom,
      };
    }
  }
};

const getAnchorWindowOffset = (
  event: GestureResponderEvent | undefined,
  topSafeArea: number,
) => {
  const nativeEvent = event?.nativeEvent as
    | TouchEventWithScreenPosition
    | undefined;
  if (Platform.OS === 'android') {
    // Android measurement removes this inset; Modal content adds it back.
    return { x: 0, y: topSafeArea };
  }
  if (Platform.OS !== 'ios') {
    return { x: 0, y: 0 };
  }
  if (nativeEvent?.pageX === undefined || nativeEvent.pageY === undefined) {
    return { x: 0, y: 0 };
  }

  return {
    x: (nativeEvent.screenX ?? nativeEvent.pageX) - nativeEvent.pageX,
    y: (nativeEvent.screenY ?? nativeEvent.pageY) - nativeEvent.pageY,
  };
};

export const Menu = ({
  anchor,
  buttonConfig,
  iconButtonConfig,
  onDismiss,
  options,
  visible,
  width = 250,
  x,
  y,
  parentBounds,
  height,
  disabled,
  ref,
}: MenuProps) => {
  const dispatch = useDispatch();
  const [internalVisible, setInternalVisible] = React.useState(false);
  const [modalVisible, setModalVisible] = React.useState(false);
  const animationProgress = useSharedValue(0);
  // Run an option only after its modal presentation has finished dismissing.
  const pendingOptionPress = React.useRef<
    { presentationId: number; callback: () => void } | undefined
  >(undefined);
  const [presentationId, setPresentationId] = React.useState(0);
  const presentationIdRef = React.useRef(0);
  const [isIOSDismissPending, setIsIOSDismissPending] = React.useState(false);
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const maxMenuWidth = windowWidth - theme.padding * 2;
  const menuWidth = Math.min(width, maxMenuWidth);
  const [menuContentDims, setMenuContentDims] = React.useState({
    width: 0,
    height: 0,
  });
  // Each opening stays hidden until its content size gives us a final position.
  const [hasMeasuredMenuContent, setHasMeasuredMenuContent] =
    React.useState(false);
  const [measurementKey, setMeasurementKey] = React.useState(0);
  const wasVisibleRef = React.useRef(false);
  const anchorRef = React.useRef<View>(null);
  const anchorMeasurementRequestRef = React.useRef(0);
  const [internalAnchorBounds, setInternalAnchorBounds] =
    React.useState<AnchorBounds>();
  const { top: topSafeArea, bottom: bottomSafeArea } = useSafeAreaInsets();
  // Parent bounds sit flush; explicit x/y use the directional press offset.
  const anchorBounds: AnchorBounds | undefined =
    x !== undefined && y !== undefined
      ? { type: 'point', left: x, right: x, top: y, bottom: y }
      : parentBounds
        ? { type: 'parent', ...parentBounds }
        : internalAnchorBounds;
  const anchorOffsetX =
    anchorBounds?.type === 'point' ? MENU_ANCHOR_OFFSET_X : 0;
  const anchorOffsetY =
    anchorBounds?.type === 'point' ? MENU_ANCHOR_OFFSET_Y : 0;

  const allowedMaxHeight = Math.min(
    height || Infinity,
    windowHeight - (topSafeArea + bottomSafeArea + 40),
  );
  let allowedPositionedMaxHeight = allowedMaxHeight;
  const measuredMenuHeight =
    menuContentDims.height > 0
      ? Math.min(menuContentDims.height, allowedMaxHeight)
      : 0;
  const measuredMenuWidth =
    menuContentDims.width > 0
      ? Math.min(menuContentDims.width, maxMenuWidth)
      : menuWidth;

  const minMenuLeft = theme.padding;
  const maxMenuRight = windowWidth - theme.padding;
  const maxMenuLeft = Math.max(minMenuLeft, maxMenuRight - measuredMenuWidth);
  let menuLeft: number | undefined;
  if (anchorBounds) {
    const menuRightOpeningLeft = anchorBounds.left - anchorOffsetX;
    const shouldPositionLeft =
      menuRightOpeningLeft + measuredMenuWidth > maxMenuRight;
    const requestedMenuLeft = shouldPositionLeft
      ? anchorBounds.right - measuredMenuWidth + anchorOffsetX
      : menuRightOpeningLeft;
    menuLeft = Math.min(Math.max(requestedMenuLeft, minMenuLeft), maxMenuLeft);
  }

  const minMenuTop = topSafeArea + 40;
  const maxMenuBottom = windowHeight - bottomSafeArea - 20;
  let menuTop: number | undefined;
  if (anchorBounds) {
    const menuTopBelow = anchorBounds.bottom + anchorOffsetY;
    const availableHeightBelow = Math.max(0, maxMenuBottom - menuTopBelow);
    const availableHeightAbove = Math.max(
      0,
      anchorBounds.top - anchorOffsetY - minMenuTop,
    );
    const shouldPositionAbove =
      measuredMenuHeight > 0 &&
      measuredMenuHeight > availableHeightBelow &&
      availableHeightAbove > availableHeightBelow;

    if (shouldPositionAbove) {
      allowedPositionedMaxHeight = Math.min(
        allowedPositionedMaxHeight,
        availableHeightAbove,
      );
      menuTop =
        anchorBounds.top -
        anchorOffsetY -
        Math.min(measuredMenuHeight, allowedPositionedMaxHeight);
    } else {
      if (measuredMenuHeight > 0) {
        allowedPositionedMaxHeight = Math.min(
          allowedPositionedMaxHeight,
          availableHeightBelow,
        );
      }
      menuTop = menuTopBelow;
    }
  }

  const handleOpen = React.useCallback(
    (event: GestureResponderEvent) => {
      hapticSelection();
      const pressPosition = getMenuAnchorPosition(event);
      // Normalize parent measurement into the menu modal's coordinate space.
      const anchorWindowOffset = getAnchorWindowOffset(event, topSafeArea);
      const measurementRequest = anchorMeasurementRequestRef.current + 1;
      anchorMeasurementRequestRef.current = measurementRequest;
      const anchorView = anchorRef.current;
      if (!anchorView) {
        if (!pressPosition) {
          return;
        }
        setInternalAnchorBounds(
          pressPosition.type === 'point'
            ? {
                type: 'point',
                left: pressPosition.x,
                right: pressPosition.x,
                top: pressPosition.y,
                bottom: pressPosition.y,
              }
            : pressPosition,
        );
        setInternalVisible(true);
        return;
      }

      // Open only after measuring so a previous anchor position is never reused.
      anchorView.measureInWindow(
        (measuredX, measuredY, measuredWidth, measuredHeight) => {
          if (measurementRequest !== anchorMeasurementRequestRef.current) {
            return;
          }
          setInternalAnchorBounds({
            type: 'parent',
            left: measuredX + anchorWindowOffset.x,
            right: measuredX + measuredWidth + anchorWindowOffset.x,
            top: measuredY + anchorWindowOffset.y,
            bottom: measuredY + measuredHeight + anchorWindowOffset.y,
          });
          setInternalVisible(true);
        },
      );
    },
    [topSafeArea],
  );

  const internalAnchorContent =
    anchor ??
    (buttonConfig ? (
      <Button
        {...buttonConfig}
        disabled={disabled || buttonConfig.disabled}
        onPress={disabled ? undefined : handleOpen}
      />
    ) : iconButtonConfig ? (
      <IconButton
        {...iconButtonConfig}
        onPress={disabled ? undefined : handleOpen}
      />
    ) : null);

  const internalAnchor: React.ReactElement | null = internalAnchorContent ? (
    <View ref={anchorRef} collapsable={false}>
      {internalAnchorContent}
    </View>
  ) : null;

  const requestDismiss = React.useCallback(() => {
    anchorMeasurementRequestRef.current += 1;
    setInternalVisible(false);
    onDismiss?.();
  }, [onDismiss]);

  const completeDismiss = React.useCallback(
    (completedPresentationId: number) => {
      // Ignore a late native dismissal from an older modal presentation.
      if (completedPresentationId !== presentationIdRef.current) {
        return;
      }
      setIsIOSDismissPending(false);
      const pendingOption = pendingOptionPress.current;
      pendingOptionPress.current = undefined;
      if (pendingOption?.presentationId === completedPresentationId) {
        pendingOption.callback();
      }
    },
    [],
  );

  React.useImperativeHandle(
    ref,
    () => ({
      close: requestDismiss,
    }),
    [requestDismiss],
  );

  const isVisible = !!visible || internalVisible;
  const isOpening = isVisible && !wasVisibleRef.current;
  const isMenuReady = hasMeasuredMenuContent && !isOpening;

  React.useLayoutEffect(() => {
    if (isOpening) {
      pendingOptionPress.current = undefined;
    }
  }, [isOpening]);

  React.useEffect(() => {
    const opening = isVisible && !wasVisibleRef.current;
    wasVisibleRef.current = isVisible;

    if (isVisible) {
      // iOS must finish dismissing the old controller before presenting again.
      if (Platform.OS === 'ios' && isIOSDismissPending) {
        return;
      }
      if (modalVisible) {
        if (opening) {
          setHasMeasuredMenuContent(false);
          setMeasurementKey((current) => current + 1);
          animationProgress.value = 0;
        }
        return;
      }
      const nextPresentationId = presentationIdRef.current + 1;
      presentationIdRef.current = nextPresentationId;
      setPresentationId(nextPresentationId);
      setHasMeasuredMenuContent(false);
      setMeasurementKey((current) => current + 1);
      setModalVisible(true);
      animationProgress.value = 0;
      return;
    }

    if (!modalVisible) {
      return;
    }

    animationProgress.value = withTiming(0, {
      duration: MENU_ANIMATION_DURATION,
      easing: Easing.bezier(0.4, 0, 0.2, 1),
    });
    const dismissTimeout = setTimeout(() => {
      if (Platform.OS === 'ios') {
        setIsIOSDismissPending(true);
      }
      setModalVisible(false);
    }, MENU_ANIMATION_DURATION);

    return () => {
      clearTimeout(dismissTimeout);
    };
  }, [animationProgress, isIOSDismissPending, isVisible, modalVisible]);

  React.useEffect(() => {
    return () => {
      pendingOptionPress.current = undefined;
      anchorMeasurementRequestRef.current += 1;
    };
  }, []);

  React.useEffect(() => {
    if (!isVisible || !isMenuReady) {
      return;
    }
    animationProgress.value = withTiming(1, {
      duration: MENU_ANIMATION_DURATION,
      easing: Easing.bezier(0.4, 0, 0.2, 1),
    });
  }, [animationProgress, isMenuReady, isVisible]);

  React.useEffect(() => {
    if (Platform.OS === 'ios' || modalVisible || isVisible) {
      return;
    }

    // onDismiss is iOS-only; elsewhere, allow teardown two frames to settle.
    let completionFrame: number | undefined;
    const afterCommitFrame = requestAnimationFrame(() => {
      completionFrame = requestAnimationFrame(() => {
        completeDismiss(presentationId);
      });
    });

    return () => {
      cancelAnimationFrame(afterCommitFrame);
      if (completionFrame !== undefined) {
        cancelAnimationFrame(completionFrame);
      }
    };
  }, [completeDismiss, isVisible, modalVisible, presentationId]);

  const animatedMenuStyle = useAnimatedStyle(() => {
    const visibleProgress = isMenuReady ? animationProgress.value : 0;
    return {
      opacity: visibleProgress,
      transform: [
        {
          scale: 0.94 + visibleProgress * 0.06,
        },
      ],
    };
  }, [isMenuReady]);

  const handleContextPress = React.useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      requestDismiss();
    },
    [requestDismiss],
  );

  React.useEffect(() => {
    if (Platform.OS === 'web' && isVisible) {
      document.addEventListener('contextmenu', handleContextPress);
    }
    return () => {
      if (Platform.OS === 'web' && isVisible) {
        document.removeEventListener('contextmenu', handleContextPress);
      }
    };
  }, [handleContextPress, isVisible]);

  React.useEffect(() => {
    if (!isVisible) {
      return;
    }

    const closeEvent = doAddEvent(eventCloseMenus, requestDismiss);
    return () => doRemoveEvents([closeEvent]);
  }, [isVisible, requestDismiss]);

  let hasOptionAbove = false;
  const bold = /\*(.*?)\*/gm;

  return (
    <>
      {internalAnchor}
      {/* Web handles Escape before Modal.onRequestClose, so claim it here. */}
      {Platform.OS === 'web' && modalVisible ? (
        <MenuEscapeHandler onDismiss={requestDismiss} />
      ) : null}
      <Modal
        key={presentationId}
        visible={modalVisible}
        onDismiss={() => completeDismiss(presentationId)}
        onRequestClose={Platform.OS === 'web' ? undefined : requestDismiss}
        supportedOrientations={[
          'portrait',
          'portrait-upside-down',
          'landscape',
          'landscape-left',
          'landscape-right',
        ]}
        transparent
      >
        <TouchableWithoutFeedback onPress={requestDismiss} accessible={false}>
          <View style={StyleSheet.absoluteFill} />
        </TouchableWithoutFeedback>
        <Animated.View
          style={[
            styles.menu,
            {
              left: menuLeft ?? theme.padding,
              top: menuTop ?? minMenuTop,
              maxWidth: maxMenuWidth,
            },
            animatedMenuStyle,
            disabled ? styles.menuDisabled : undefined,
            !isMenuReady ? styles.menuMeasuring : undefined,
          ]}
        >
          <ScrollView
            key={measurementKey}
            style={{
              maxHeight: allowedPositionedMaxHeight,
              pointerEvents: 'auto',
            }}
            onContentSizeChange={(contentWidth, contentHeight) => {
              setMenuContentDims((current) =>
                current.width === contentWidth &&
                current.height === contentHeight
                  ? current
                  : { width: contentWidth, height: contentHeight },
              );
              setHasMeasuredMenuContent(true);
            }}
          >
            <ViewWithSeparators>
              {options.map((option, index, array) => {
                if (option === '-') {
                  const nextOption = array[index + 1];
                  if (hasOptionAbove && nextOption && nextOption !== '-') {
                    hasOptionAbove = false;
                    return (
                      <SeparatorHorizontal
                        key={`separator-${index}`}
                        style={{
                          backgroundColor:
                            Platform.OS === 'ios' ? '#999' : '#ddd',
                          height:
                            Platform.OS === 'ios'
                              ? StyleSheet.hairlineWidth
                              : 1,
                          marginTop: -(Platform.OS === 'ios'
                            ? StyleSheet.hairlineWidth
                            : 1),
                          marginBottom: -(Platform.OS === 'ios'
                            ? StyleSheet.hairlineWidth
                            : 1),
                        }}
                      />
                    );
                  }
                  return null;
                }
                hasOptionAbove = true;
                if (typeof option === 'string') {
                  return (
                    <View
                      key={option}
                      style={{
                        padding: theme.padding,
                        width: menuWidth,
                        backgroundColor: '#f1f1f1',
                        borderTopWidth: 1,
                        borderBottomWidth: 1,
                        borderColor: '#eaeaea',
                      }}
                    >
                      <Txt size="xxxsmall" weight="bold">
                        {option}
                      </Txt>
                    </View>
                  );
                }

                if (React.isValidElement(option)) {
                  return option;
                }

                if (!isDefinedMenuOption(option)) {
                  return null;
                }
                const optionOnPress = option.onPress;

                return (
                  <Pressable
                    key={option.key || option.text}
                    style={{
                      padding: theme.padding,
                      width: menuWidth,
                      minHeight: 44,
                    }}
                    disabled={disabled || !optionOnPress}
                    hoveredStyle={{ backgroundColor: '#f1f1f1' }}
                    pressedStyle={{ backgroundColor: '#dddddd' }}
                    onPress={
                      optionOnPress
                        ? () => {
                            pendingOptionPress.current = {
                              presentationId,
                              callback: optionOnPress,
                            };
                            dispatch(eventCloseMenus());
                          }
                        : undefined
                    }
                  >
                    <ViewWithSeparators
                      horizontal
                      separatorMargin={theme.padding / 2}
                      separatorColor="transparent"
                      style={{ flex: 1, alignItems: 'center' }}
                    >
                      {option.leftIcon && (
                        <Icon
                          source={option.leftIcon}
                          color={option.leftIconColor}
                          size={12}
                          style={!optionOnPress ? { opacity: 0.42 } : undefined}
                        />
                      )}
                      <View
                        style={[
                          { flex: 1 },
                          option.horizontalText
                            ? { flexDirection: 'row', alignItems: 'center' }
                            : undefined,
                        ]}
                      >
                        <Txt
                          size={option.textSize}
                          style={[
                            option.destructive || option.textColor
                              ? {
                                  color: option.textColor || theme.colors.red,
                                }
                              : undefined,
                            optionOnPress ? undefined : { opacity: 0.42 },
                            { flex: 1 },
                          ]}
                          numberOfLines={option.textNumberOfLines ?? 1}
                        >
                          {reactStringReplace(option.text, bold, (match, i) => (
                            <Txt key={i} weight="bold" size={option.textSize}>
                              {match}
                            </Txt>
                          ))}
                        </Txt>
                        {!!option.subText && (
                          <Txt
                            size={option.horizontalText ? undefined : 'xsmall'}
                            style={[
                              {
                                color: theme.colors.fieldValueText,
                              },
                              option.horizontalText
                                ? { textAlign: 'right' }
                                : { marginTop: 3, flex: 1 },
                            ]}
                          >
                            {reactStringReplace(
                              option.subText,
                              bold,
                              (match, i) => (
                                <Txt key={i} weight="bold">
                                  {match}
                                </Txt>
                              ),
                            )}
                          </Txt>
                        )}
                      </View>
                      {option.icon && (
                        <Icon
                          source={option.icon}
                          color={
                            option.iconColor ||
                            (option.destructive ? theme.colors.red : undefined)
                          }
                          style={!optionOnPress ? { opacity: 0.42 } : undefined}
                        />
                      )}
                    </ViewWithSeparators>
                  </Pressable>
                );
              })}
            </ViewWithSeparators>
          </ScrollView>
        </Animated.View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  menu: {
    position: 'absolute',
    opacity: 0,
    borderRadius: 10,
    overflow: 'hidden',
    boxShadow: [
      {
        color: theme.colors.black30,
        offsetX: 0,
        offsetY: 0,
        blurRadius: 50,
      },
    ],
    backgroundColor: '#fafafa',
  },
  menuMeasuring: { opacity: 0, pointerEvents: 'none' },
  menuDisabled: { opacity: 0.42, pointerEvents: 'none' },
});
